import React, { useContext, useMemo, useCallback, useState } from "react";
import * as yup from "yup";
import { Formik, Form } from "formik";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTheme } from "styled-components";
import { Flex, Box } from "reflexbox";
import formatter from "../../modules/formatter";
import {
  createUser,
  emailNewUserPassword,
} from "../../requests/userManagementRequests";
import { login } from "../../requests/authRequests";
import { Heading, Body } from "../UI/Text";
import Button from "../UI/Button";
import Card from "../UI/Card";
import Modal from "../UI/Modal";
import Field from "../UI/forms/Field";
import UserContext from "../../context/user/UserContext";
import Table from "../UI/Table";
import { useEffect } from "react";
import guid from "../../modules/guid";
import Loading from "../UI/Loading";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";

const validationSchema = yup.object().shape({
  username: yup.string().email(),
});

export const CreateUserModal = ({
  show,
  setShow,
  finishConfiguration,
  sessionId,
}) => {
  const { buildUserObject } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const [isLoading, setLoading] = useState(false);
  const theme = useTheme();

  const onSubmit = useCallback(
    async ({ username }, signal) => {
      if (!username) return;
      setLoading(true);
      const newPassword = guid.GenerateGUID();
      await createUser({
        input: {
          username,
          password: newPassword,
          roleId: "public",
          roleLevel: 0,
          checkoutType: "stripe",
        },
      });
      await emailNewUserPassword({ username, password: newPassword });
      await login({ username, password: newPassword });
      await finishConfiguration({ sessionId, username, publicUser: true });
      await buildUserObject(username, signal);
      navigate("/Catalog");
    },
    [buildUserObject, finishConfiguration, sessionId, navigate]
  );

  useEffect(() => {
    if (show) {
      if (email) {
        const controller = new AbortController();

        setShow(false);
        onSubmit({ username: email }, controller.signal);
        return () => controller.abort();
      }
    }
  }, [email, onSubmit, setShow, show]);

  return (
    <Modal show={show} setShow={setShow}>
      <Card maxWidth="400px">
        <Heading center mb="2rem">
          Please enter your email to save your configuration.
        </Heading>
        <Formik
          {...{ validationSchema, onSubmit, initialValues: { username: "" } }}
        >
          {() => (
            <Form>
              <Flex alignItems="center" mb="1.5rem">
                <Field
                  m="0 !important"
                  style={{ minWidth: "200px" }}
                  noLabel
                  name="username"
                />
                <Button
                  ml="1rem"
                  size="large"
                  type="submit"
                  iconName={isLoading ? undefined : "faFloppyDisk"}
                >
                  {isLoading ? (
                    <Loading
                      inline
                      color1={theme.secondary}
                      color2={theme.success}
                    />
                  ) : null}
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Card>
    </Modal>
  );
};

export const CancelModal = ({ show, setShow }) => {
  const { quoteId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { loggedIn, activeUser } = useContext(UserContext);

  const redirect = useCallback(() => {
    if (!loggedIn) return navigate("/Login");
    else if (activeUser?.roleLevel === 0) return navigate("/");
    else if (quoteId === "new") return navigate("/QuoteDashboard");
    else return navigate(`/Quote/${quoteId}`);
  }, [activeUser, loggedIn, navigate, quoteId]);

  return (
    <Modal show={show} setShow={setShow}>
      <Card maxWidth="300px">
        <Heading center mb="2rem">
          Are you sure you want to cancel?
        </Heading>
        <Flex mb="1.5rem">
          <Button onClick={redirect} iconName="faCheck" />
          <Button
            color={theme.gray}
            onClick={() => setShow(false)}
            iconName="faXmark"
          />
        </Flex>
      </Card>
    </Modal>
  );
};

export const SummaryModal = ({ show, setShow, Details, ConfiguredPrice }) => {
  const theme = useTheme();
  console.log({Details});
  
  const columns = useMemo(
    () => [
      {
        id: "Caption",
        data: (row) => (
          <Body bold flex={1}>
            {row.Caption}
          </Body>
        ),
        header: "Part",
        sort: false,
        size: { md: 7, sm: 7, xs: 7 },
      },
      {
        id: "value",
        data: (row) => (
          <Body right color={theme.secondary} flex={1}>
            {row.value}
          </Body>
        ),
        header: "Selection",
        sort: false,
        size: { md: 3, sm: 3, xs: 3 },
      },
    ],
    [theme.secondary]
  );

  const gridData = useMemo(() => {
    const items = [];
    Details.forEach((Detail) => {
      const { Value, Type, Caption: _Caption } = Detail || {};      
      if (
        //(!Value && Value !== false) ||
        Value?.startsWith("<hr") ||
        _Caption?.startsWith("<hr")
      )
        return;
      const [caption, ...rest] = _Caption.split(":");

      console.log({caption});

      const regex = /(<([^>]+)>)/gi;   
      const cleansedCaption = caption ? caption.replace(regex, "") : caption;  
      console.log({cleansedCaption, caption});

      if (!cleansedCaption) return;
      const Caption = (
        <Body>
          {cleansedCaption}:{" "}
          <span style={{ color: theme.secondary }}>
            {typeof rest === "string" && rest?.startsWith("$")
              ? `$${formatter.currency(rest)}`
              : rest}
          </span>
        </Body>
      );

      // switch (priceCode) {
      //   case "DEALER":
      //     break;
      //   case "MSRP":
      //     const value = Type >= 5 ? `$${formatter.currency(Value)}` : Value;
       
     

      if (!cleansedCaption.includes("Height Image")) items.push({ Caption, value: Value });
      //     break;
      //   default:
      //     break;
      // }
    });

    console.log({items});
    return items;
  }, [Details, theme.secondary]);

  return (
    <Modal show={show} setShow={setShow}>
      <Card>
        <Flex
          style={{ gap: "1rem" }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Button
            iconName="faXmark"
            style={{ opacity: 0, pointerEvents: "none" }}
          />
          <Heading my={0} center>
            Configuration Details
          </Heading>
          <Button iconName="faXmark" onClick={() => setShow(false)} />
        </Flex>
        <Body mt="1rem" color={theme.secondary} bold center>
          Total Price: ${formatter.currency(ConfiguredPrice)}
        </Body>
        <Box
          style={{
            overflowY: "scroll",
            overflowX: "hidden",
          }}
          maxHeight="500px"
          maxWidth="700px"
          width={1}
          flexDirection="column"
          flexWrap="wrap"
          id="columnScrollable"
        >
          <Table
            hideBack
            hideCreate
            columns={columns}
            totalCount={gridData.length}
            data={gridData}
            hidePager
            hideProperties
            hideSearch
            hideToggle
          />
        </Box>
      </Card>
    </Modal>
  );
};
